import type { TMessage } from "@asurion-core/headless-chat-web";

import { useReactAnalytics } from "@asurion-core/eventualize-react";
import { useMessagesStore } from "@asurion-core/headless-chat-web";
import { useEffect, useEffectEvent, useMemo } from "react";

import type { GuideSelectionMessage, GuideTextMessage, UiMetadata } from "../logic-builder";

import { isUiMetadataMessage, isValidGuideMessage } from "../logic-builder";
import { getGuideAnalyticDataFromMessage, isGuideSelectionMessage } from "../logic-builder/logicBuilderResolvers";
import { selectGuideSession, useGuideStore } from "../store";

interface ParseMessagesProps {
    employeeId?: string;
    sessionId: string;
}

interface ParseMessagesResult {
    latestMessage: TMessage | undefined;
    messages: TMessage[];
}

export function useParseMessages({ employeeId, sessionId }: ParseMessagesProps): ParseMessagesResult {
    const { dispatcher } = useReactAnalytics();

    // message store contains all history from the logic builder, including:
    // - messages from the bot (content (current step) + options for a guide step)
    // - messages from the expert (selected option from the guide step)
    const messages = useMessagesStore((s) => s.messages);

    /*
       latestValidMessage is the last message from the bot,
       which correlates to the most recent step in the guide
       if we have no valid step to display, we return no guide
    */
    const latestValidMessage = messages.findLast(isValidGuideMessage);

    // TODO: accept uiMetadata with a CUSTOM message type
    // [EWS-1094](https://solutonashville.atlassian.net/browse/EWS-1094)
    const latestUiMessage = useMemo(() => {
        return messages.findLast((m) => {
            return isUiMetadataMessage(m);
        }) as GuideTextMessage | undefined;
    }, [messages]);
    // sessionId is in the trigger deps to avoid stale-closure writes during chat switches,
    // but setCurrentGuideName has analytics side effects (Guide_OpenGuide dispatch), so we
    // short-circuit when the store already holds the incoming flowName for this session —
    // otherwise switching back to a chat with a guide in progress re-fires the event.
    const syncGuideStateForSession = useEffectEvent(() => {
        const state = useGuideStore.getState();
        const customMetadata: UiMetadata | undefined = latestUiMessage?.data?.customData;
        state.setUiMetadata(sessionId, customMetadata);
        const currentInStore = selectGuideSession(state, sessionId).currentGuideName;
        if (currentInStore !== customMetadata?.flowName) {
            state.setCurrentGuideName(sessionId, { dispatcher, currentGuideName: customMetadata?.flowName });
        }
    });
    useEffect(() => {
        syncGuideStateForSession();
        // oxlint-disable-next-line exhaustive-deps
    }, [latestUiMessage?.data?.customData?.flowName, sessionId]);

    // Set available guides on every guide selection message
    const latestGuideSelectionMessage = useMemo(() => {
        return messages.findLast((m) => {
            return isGuideSelectionMessage(m);
        }) as GuideSelectionMessage | undefined;
    }, [messages]);
    useEffect(() => {
        const availableGuides = latestGuideSelectionMessage?.data?.guideSelection;
        const filteredGuides = availableGuides?.filter((guide) => {
            const { employeeIds } = guide;
            const isEmployeeIdValid = !employeeIds || (employeeId && employeeIds.includes(employeeId));
            return isEmployeeIdValid;
        });
        if (filteredGuides) {
            useGuideStore.getState().setAvailableGuides(sessionId, filteredGuides);
        }
    }, [employeeId, latestGuideSelectionMessage, sessionId]);

    // Update analytics info on every message update
    const analyticsInfo = useMemo(() => {
        return getGuideAnalyticDataFromMessage(latestValidMessage, messages);
    }, [latestValidMessage, messages]);
    useEffect(() => {
        useGuideStore.getState().setAnalyticsInfo(sessionId, analyticsInfo);
    }, [analyticsInfo, sessionId]);

    // Clear waiting state when latestValidMessage changes (new BOT message received)
    useEffect(() => {
        useGuideStore.getState().setIsWaitingForNextStep(sessionId, false);
    }, [latestValidMessage, sessionId]);

    return {
        latestMessage: latestValidMessage,
        messages,
    };
}
